import { useGraphqlPagination } from '@components/infiniteScroll';
import { GetMembersQuery, Member, useGetMembersQuery } from '@generated/graphql';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react';

const INITIAL_FETCH_LIMIT = 100;

type MemberLookupFn = (email: string) => Promise<Member>;

type PendingLookup = {
  resolve: (member: Member) => void;
  reject: (error: Error) => void;
};

const MemberLookupContext = createContext<MemberLookupFn>(() =>
  Promise.reject(new Error('MemberLookupContext not initialized')),
);

export const MemberLookupProvider = ({ children }: PropsWithChildren) => {
  const { organizationId } = useOrganizationContext();
  const pendingLookupsRef = useRef<Map<string, PendingLookup>>(new Map());
  const membersRef = useRef<Member[]>([]);
  const hasMoreRef = useRef(true);

  const {
    data: members,
    hasMore,
    fetchMore,
    loading,
  } = useGraphqlPagination<GetMembersQuery, Member>(
    useGetMembersQuery({
      variables: { organizationId, limit: INITIAL_FETCH_LIMIT },
      skip: !organizationId,
    }),
    'getMembers',
    'userId',
  );

  // Keep refs in sync with reactive state
  membersRef.current = members;
  hasMoreRef.current = hasMore;

  // Resolve or reject pending lookups when data changes
  useEffect(() => {
    const pending = pendingLookupsRef.current;
    const emailsToRemove: string[] = [];

    pending.forEach((lookup, email) => {
      const found = members.find((m) => m.email === email);
      if (found) {
        lookup.resolve(found);
        emailsToRemove.push(email);
      }
    });
    emailsToRemove.forEach((email) => pending.delete(email));

    if (!hasMore) {
      pending.forEach((lookup, email) => {
        lookup.reject(new Error(`Member with email "${email}" not found`));
      });
      pending.clear();
    }
  }, [members, hasMore]);

  // Fetch next page when there are unresolved lookups
  useEffect(() => {
    if (pendingLookupsRef.current.size > 0 && hasMore && !loading) {
      void fetchMore();
    }
  }, [members, hasMore, loading, fetchMore]);

  // Clear pending lookups when organization changes
  useEffect(() => {
    const pending = pendingLookupsRef.current;
    pending.forEach((lookup, email) => {
      lookup.reject(new Error(`Organization changed, lookup for "${email}" cancelled`));
    });
    pending.clear();
  }, [organizationId]);

  // Stable function ref that never changes identity — reads from refs instead of closures
  const getMemberByEmailRef = useRef<MemberLookupFn>((email) => {
    const found = membersRef.current.find((m) => m.email === email);
    if (found) return Promise.resolve(found);

    if (!hasMoreRef.current) {
      return Promise.reject(new Error(`Member with email "${email}" not found`));
    }

    const existing = pendingLookupsRef.current.get(email);
    if (existing) {
      return new Promise((resolve, reject) => {
        const original = existing;
        pendingLookupsRef.current.set(email, {
          resolve: (member) => {
            original.resolve(member);
            resolve(member);
          },
          reject: (error) => {
            original.reject(error);
            reject(error);
          },
        });
      });
    }

    return new Promise<Member>((resolve, reject) => {
      pendingLookupsRef.current.set(email, { resolve, reject });
    });
  });

  return (
    <MemberLookupContext.Provider value={getMemberByEmailRef.current}>
      {children}
    </MemberLookupContext.Provider>
  );
};

export const useMemberLookup = (email: string | null | undefined, fallback = 'Unknown') => {
  const getMemberByEmail = useContext(MemberLookupContext);
  const [name, setName] = useState(email ?? fallback);

  useEffect(() => {
    if (!email) {
      setName(fallback);
      return () => {};
    }

    let cancelled = false;
    setName(email);
    getMemberByEmail(email)
      .then((member) => {
        if (!cancelled) setName(member.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [email, fallback, getMemberByEmail]);

  return name;
};
